import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { Formik } from "formik";
import { IDetails } from "us.collection.admin/components/ManageFee/Interfaces";
import {
  ManageFeeAction,
  INITIAL_DRAWER_DATA,
  DebtorType,
  DEBTOR_TYPE_OPTIONS,
} from "us.collection.admin/components/ManageFee/Constants";
import { useLocation } from "react-router-dom";
import { FeeTypeValidationSchema } from "us.collection.admin/components/ManageFee/Validations";
import { AddNewFeeBasisSW, AddNewFeeTypeSW } from "us.collection.admin/components/ManageFee/Repository";
import * as Actions from "us.collection.admin/actions";
import { checkFeeTypeDuplicate } from "us.collection.admin/components/ManageFee/Functions";
import "../../Home.scss";
import { $DatePicker, $Select } from "us.common/components";
import { RootState } from "us.helper/types";
import { initialValues } from "./Constants";
import dayjs from "dayjs";

const { $Button, $Form, $TextArea, $Popconfirm, $AsyncInput } =
  Common.Components;

/**
 * @description - Fee Type Detail view
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3116171308/Manage+Fee+-+UI+Implementation+Design
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 26/08/2025
 */
const DetailsSW: React.FC<PropsFromRedux & IDetails> = (props) => {
  const { t } = useTranslation();
  const {
    actionType,
    addNewFeeType,
    changeDrawerDetails,
    savePayload,
    feeType,
    currentDateFormat
  } = props;

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const typeId = urlParams.get("typeId");
  const feeId = urlParams.get("feeId");

  /**
   * @description - Handle form submit
   * @param {any} values - Form values
   * @param {any} actions - Form Actions
   */
  const handleSubmit = (values: any, actions: any) => {
    actions.setSubmitting(true);
    const typePayload = AddNewFeeTypeSW.call({ ...values, typeId, feeId });

    const basisPayload = AddNewFeeBasisSW.call({
          ...values,
          typeId,
          feeId: feeId,
        });
    if (actionType === ManageFeeAction.ADD) {
      addNewFeeType && addNewFeeType({typePayload, basisPayload});
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={FeeTypeValidationSchema}
      validateOnBlur
      validateOnChange
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        ...restProps
      }: any) => (
        <>
          <$Form layout="vertical" autoComplete="off" onSubmit={handleSubmit}>
            <div>
              <div className="mb-3">
                <$AsyncInput
                  name="feeTypeName"
                  label={t("US.COLLECTION.ADMIN:MANAGE_FEE.FEE_TYPE")}
                  className="w-100"
                  required
                  dataTestid="async-input-remitJournalPrifix"
                  placeholder={t("US.COLLECTION.ADMIN:MANAGE_FEE.FEE_TYPE")}
                  size="small"
                  autoFocus={false}
                  isValid={checkFeeTypeDuplicate(
                    feeType?.list,
                    values.feeTypeName
                  )}
                  asyncError={t(
                    "US.COLLECTION.VALIDATIONS:INVALID.FEE_TYPE_ALREADY_EXISTS"
                  )}
                />
              </div>
              <div className="mb-3">
                <$TextArea
                  label={t("US.COLLECTION.ADMIN:MANAGE_FEE.DESCRIPTION")}
                  name="description"
                  autoSize={{ minRows: 8, maxRows: 8 }}
                  required
                />
              </div>
              <div className="mb-3">
                <$DatePicker
                  allowClear
                  label={t("US.COLLECTION.COMMON:COMMON.DATE")}
                  placeholder={t("US.COLLECTION.COMMON:COMMON.SELECT_DATE")}
                  name="fromDate"
                  format={currentDateFormat}
                  size="small"
                  defaultPickerValue={dayjs()}
                  value={dayjs(values?.fromDate)}
                  className="w-100"
                  required
                  data-testid="fee-date"
                />
              </div>
              <div>
                <$Select
                  name="debtorType"
                  formitem={{
                    label: t("US.COLLECTION.ADMIN:MANAGE_FEE.DEBTOR_TYPE"),
                  }}
                  allOption={false}
                  required
                  defaultValue={DebtorType.P}
                  options={DEBTOR_TYPE_OPTIONS}
                  data-testid="debtor_Type"
                />
              </div>
            </div>
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  type="primary"
                  onClick={handleSubmit}
                  className="mr-2"
                  data-testid="save-btn"
                  loading={savePayload?.isSaving}
                  disabled={
                    !checkFeeTypeDuplicate(feeType?.list, values.feeTypeName) ||
                    Object.keys(values).length === 0 ||
                    !restProps.isValid
                  }
                >
                  {t("US.COMMON:COMMON.SAVE")}
                </$Button>
                {Object.keys(values).length !== 0 && (
                  <$Popconfirm
                    title={t(
                      "US.COMMON:COMMON.ARE_YOU_SURE_YOU_WANT_TO_DISCARD_THE_CHANGES_?"
                    )}
                    placement="topLeft"
                    onConfirm={() =>
                      changeDrawerDetails &&
                      changeDrawerDetails(INITIAL_DRAWER_DATA)
                    }
                    okText={t("US.COMMON:COMMON.YES")}
                    cancelText={t("US.COMMON:COMMON.NO")}
                  >
                    <$Button>{t("US.COMMON:COMMON.CANCEL")}</$Button>
                  </$Popconfirm>
                )}
                {Object.keys(values).length === 0 && (
                  <$Button
                    onClick={() =>
                      changeDrawerDetails &&
                      changeDrawerDetails(INITIAL_DRAWER_DATA)
                    }
                  >
                    {t("US.COMMON:COMMON.CANCEL")}
                  </$Button>
                )}
              </div>
            </div>
          </$Form>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { common, manageFee } = state;
  const { currentDateFormat, currentCurrency, currentLanguage } = common;
  const { feeBasis, feeRange, savePayload, feeType } = manageFee;
  return {
    currentDateFormat,
    currentCurrency,
    currentLanguage,
    feeBasis,
    feeRange,
    feeType,
    savePayload,
  };
};
const { newFeeType, feeTypeActions, newFeeBasis } = Actions.manageFee;
const { openDrawer } = feeTypeActions;

const mapDispatchToProps = {
  addNewFeeType: newFeeType.save,
  addNewFeeBasis: newFeeBasis.save,
  changeDrawerDetails: openDrawer,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(DetailsSW);
